/**
 * P1-T06 city material plugins for Babylon PBRMaterial (UMD 9.29).
 * Patterns ported from the reference prototype (WeatherPlugin/SwayPlugin in
 * kota-nova-v3.html) into typed classes. All hooks verified against the
 * 9.29.0 bundle source: CUSTOM_VERTEX_UPDATE_POSITION, CUSTOM_FRAGMENT_*
 * ALBEDO / METALLICROUGHNESS / BEFORE_FRAGCOLOR.
 */
import { MaterialPluginBase, type Material } from 'babylonjs';
import { CityEnv } from './cityEnv';

/** Wetness darkens + sharpens up-facing surfaces; snow whitens them. */
export class WeatherPlugin extends MaterialPluginBase {
  private readonly puddles: boolean;
  constructor(material: Material, opts: { puddles?: boolean } = {}) {
    super(material, 'KNWeather', 200, { KN_WEATHER: false, KN_PUDDLE: false }, true, true);
    this.puddles = opts.puddles === true;
  }
  override prepareDefines(defines: Record<string, unknown>): void {
    defines.KN_WEATHER = true;
    defines.KN_PUDDLE = this.puddles;
  }
  override getClassName(): string {
    return 'KNWeatherPlugin';
  }
  override getUniforms(): {
    ubo: { name: string; size: number; type: string }[];
    fragment: string;
  } {
    return {
      ubo: [
        { name: 'knWet', size: 1, type: 'float' },
        { name: 'knSnow', size: 1, type: 'float' },
        { name: 'knWetTime', size: 1, type: 'float' },
      ],
      fragment:
        '#ifdef KN_WEATHER\nuniform float knWet;\nuniform float knSnow;\nuniform float knWetTime;\n#endif\n',
    };
  }
  override bindForSubMesh(ubo: { updateFloat(name: string, v: number): void }): void {
    ubo.updateFloat('knWet', CityEnv.wet);
    ubo.updateFloat('knSnow', CityEnv.snow);
    ubo.updateFloat('knWetTime', CityEnv.time);
  }
  override getCustomCode(type: string): Record<string, string> | null {
    if (type !== 'fragment') return null;
    return {
      CUSTOM_FRAGMENT_UPDATE_ALBEDO: `
#ifdef KN_WEATHER
{ float knUp = smoothstep(0.55, 0.9, vNormalW.y);
  surfaceAlbedo *= (1.0 - 0.42 * knWet * (0.35 + 0.65 * knUp));
  float knN = fract(sin(dot(floor(vPositionW.xz * 0.7), vec2(12.9898, 78.233))) * 43758.5453);
  surfaceAlbedo = mix(surfaceAlbedo, vec3(0.90, 0.92, 0.96), clamp(knSnow * knUp * (0.75 + 0.25 * knN), 0.0, 1.0));
#ifdef KN_PUDDLE
  // asphalt depressions: two-octave value-noise blobs, gated by wetness
  float knPm = knPuddleMask(vPositionW.xz);
  surfaceAlbedo *= (1.0 - 0.55 * knPm * knWet);
#endif
}
#endif
`,
      CUSTOM_FRAGMENT_UPDATE_METALLICROUGHNESS: `
#ifdef KN_WEATHER
{ float knUp2 = smoothstep(0.55, 0.9, vNormalW.y);
  metallicRoughness.g = mix(metallicRoughness.g, 0.06, knWet * 0.75 * knUp2);
  metallicRoughness.g = mix(metallicRoughness.g, 0.9, knSnow * knUp2);
#ifdef KN_PUDDLE
  // puddles are glossier than plain wet asphalt and shimmer slightly
  float knPm2 = knPuddleMask(vPositionW.xz);
  float knShim = 0.5 + 0.5 * sin(knWetTime * 0.9 + vPositionW.x * 0.35 + vPositionW.z * 0.5);
  metallicRoughness.g = mix(metallicRoughness.g, mix(0.02, 0.14, knShim), knPm2 * knWet * knUp2);
#endif
}
#endif
`,
      CUSTOM_FRAGMENT_DEFINITIONS: `
#ifdef KN_PUDDLE
float knHash21(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float knVnoise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(knHash21(i), knHash21(i + vec2(1.0, 0.0)), f.x),
             mix(knHash21(i + vec2(0.0, 1.0)), knHash21(i + vec2(1.0, 1.0)), f.x), f.y);
}
float knPuddleMask(vec2 xz) {
  float n = 0.62 * knVnoise(xz * 0.10) + 0.38 * knVnoise(xz * 0.31 + 7.31);
  return smoothstep(0.60, 0.72, n);
}
#endif
`,
    };
  }
}

/** Foliage sway (vertex offset scaling with height; prototype recipe). */
export class SwayPlugin extends MaterialPluginBase {
  constructor(material: Material) {
    super(material, 'KNSway', 201, { KN_SWAY: false }, true, true);
  }
  override prepareDefines(defines: Record<string, unknown>): void {
    defines.KN_SWAY = true;
  }
  override getClassName(): string {
    return 'KNSwayPlugin';
  }
  override getUniforms(): { ubo: { name: string; size: number; type: string }[]; vertex: string } {
    return {
      ubo: [
        { name: 'knTime', size: 1, type: 'float' },
        { name: 'knWind', size: 1, type: 'float' },
      ],
      vertex: '#ifdef KN_SWAY\nuniform float knTime;\nuniform float knWind;\n#endif\n',
    };
  }
  override bindForSubMesh(ubo: { updateFloat(name: string, v: number): void }): void {
    ubo.updateFloat('knTime', CityEnv.time);
    ubo.updateFloat('knWind', CityEnv.wind);
  }
  override getCustomCode(type: string): Record<string, string> | null {
    if (type !== 'vertex') return null;
    return {
      CUSTOM_VERTEX_UPDATE_POSITION: `
#ifdef KN_SWAY
{
#ifdef INSTANCES
  float knPh = world3.x * 0.13 + world3.z * 0.17;
#else
  float knPh = 0.0;
#endif
  float knH = max(positionUpdated.y - 1.2, 0.0);
  float knA = knWind * knH * 0.010;
  positionUpdated.x += sin(knTime * 1.6 + knPh * 0.13) * knA
                     + sin(knTime * 4.3 + knPh * 0.4 + positionUpdated.y) * knA * 0.3;
  positionUpdated.z += cos(knTime * 1.25 + knPh * 0.17) * knA * 0.7;
}
#endif
`,
    };
  }
}
